import { useEffect, useState } from 'react';

import { getStartOfMonthDate } from '@/utility/dateHelper';

import { getCommunityVisits } from '@/services/community/communitiesAnalyticsService';
import {
  fetchMembershipCounts,
  fetchMoneyPageEarnings
} from '@/services/moneyPageService';

import { showErrorToast } from '@/components/common/ToastContainer';

import { getStartEndDateFromYearAndMonth } from '../../money/utils';
import getConstants from '../constants';

function useSummaryCardSection({ activeCommunity }) {
  const { MEMBERSHIP_COUNT_INTERVAL, EARNINGS_STATES } = getConstants();
  const activeCommunityId = activeCommunity?._id;

  // States for Community Visits Card
  const [fetchingVisitsData, setFetchingVisitsData] = useState(true);
  const [visitsData, setVisitsData] = useState(null);

  // States for Member Card
  const [memberCountData, setMemberCountData] = useState([]);
  const [fetchingMemberCountData, setFetchingMemberCountData] =
    useState(true);

  // States for Earnings Card
  const [fetchingEarningsData, setFetchingEarningsData] = useState(true);
  const [earningsData, setEarningsData] = useState([]);
  const [earningsCardState, setEarningsCardState] = useState('');

  const [fetchingAllData, setFetchingAllData] = useState(true);

  const getMemberCountData = async () => {
    if (!activeCommunityId) {
      return;
    }
    setFetchingMemberCountData(true);
    const { data, error } = await fetchMembershipCounts(
      activeCommunityId,
      MEMBERSHIP_COUNT_INTERVAL
    );
    if (error) {
      showErrorToast(error);
      setFetchingMemberCountData(false);
      return;
    }
    setMemberCountData(data);
    setFetchingMemberCountData(false);
  };

  const getCommunityVisitsForLastMonth = async () => {
    setFetchingVisitsData(true);

    const payload = {
      startDate: getStartOfMonthDate(),
      communityId: activeCommunityId
    };
    const { data, error } = await getCommunityVisits(payload);

    if (error) {
      showErrorToast(error);
      setFetchingVisitsData(false);
      return;
    }

    setVisitsData(data?.data);
    setFetchingVisitsData(false);
  };

  const getEarningsAndSalesByMonth = async () => {
    setFetchingEarningsData(true);

    //Only fetch earnings data if user is a paid community or have a paid entity
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const payload = {
      ...getStartEndDateFromYearAndMonth(currentYear, currentMonth)
    };
    const { data, error } = await fetchMoneyPageEarnings(
      activeCommunityId,
      payload
    );

    if (error) {
      showErrorToast(error);
      setFetchingEarningsData(false);
      return;
    }

    // If earnings does not exist just set no paid at all
    if (!data?.earningExists) {
      setEarningsCardState(EARNINGS_STATES.NO_PAID_COMM_AND_ENTITY);
      setFetchingEarningsData(false);
      return;
    }

    setEarningsData(data);

    if (data?.overallEarnings?.total?.amount > 0) {
      setEarningsCardState(EARNINGS_STATES.HAVE_PAID_COMMU_OR_ENTITY);
    }
    if (data?.overallEarnings?.total?.amount === 0) {
      setEarningsCardState(EARNINGS_STATES.NO_SALES_THIS_MONTH);
    }

    setFetchingEarningsData(false);
  };

  const resetStates = () => {
    setEarningsData([]);
  };

  // Get all Earnings and Member count data for cards
  useEffect(() => {
    setFetchingAllData(true);
    resetStates();
    getMemberCountData();
    getEarningsAndSalesByMonth();
    getCommunityVisitsForLastMonth();
  }, [activeCommunity]);

  // Set loading state for all 3 cards
  useEffect(() => {
    if (
      !fetchingMemberCountData &&
      !fetchingEarningsData &&
      !fetchingVisitsData
    ) {
      setFetchingAllData(false);
    }
  }, [fetchingMemberCountData, fetchingEarningsData, fetchingVisitsData]);

  return {
    fetchingAllData,
    earningsData,
    earningsCardState,
    memberCountData,
    visitsData
  };
}

export default useSummaryCardSection;
